import {
    ApiReferralPrepareReqSchema,
    ApiReferralPrepareResSchema,
    ApiReferralRedeemReqSchema,
    ApiReferralRedeemResSchema,
    ApiReferralShareCompleteReqSchema,
    ApiReferralShareCompleteResSchema,
    ApiReferralStatusResSchema,
    type ApiReferralSnapshot,
} from "@bridge-applications/pb-types";
import { usePostHog } from "@posthog/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NAME_SPACE, TELEGRAM_MINI_APP_URL } from "../../state/config";
import { authenticatedApiCall } from "../helpers/api";
import { captureClientAnalytics } from "../helpers/analytics";
import { haptics } from "../helpers/haptics";
import { getTelegramWebApp, shareUrlViaTelegram } from "../helpers/telegram";
import i18n from "../i18n/i18n";
import { useRequestSlotMachineRefetch } from "./useSlotMachine";

const REFERRAL_CAMPAIGN_CODE = "telegram-share-3-opens-v1";
const REFERRAL_START_PARAM = /^ref_[A-Za-z0-9_-]{30,100}$/;
const POLL_INTERVAL_MS = 12_000;

interface PreparedShare {
    messageId: string;
    expiresAt: string;
    shareMessageId: string;
}

interface UseReferralCampaignOptions {
    enabled: boolean;
    isOpen: boolean;
    userId: string | null;
    telegramUserId: number | null;
}

export class ReferralApiError extends Error {
    readonly code: string;

    constructor(code: string, message?: string) {
        super(
            message ??
                i18n.t("errors.referralRequestFailed", {
                    code,
                }),
        );
        this.name = "ReferralApiError";
        this.code = code;
    }
}

function readStorage(key: string) {
    try {
        return window.localStorage.getItem(key);
    } catch {
        return null;
    }
}

function writeStorage(key: string, value: string) {
    try {
        window.localStorage.setItem(key, value);
    } catch {
        // Local storage is only a client hint; the server remains authoritative.
    }
}

function removeStorage(key: string) {
    try {
        window.localStorage.removeItem(key);
    } catch {
        // See writeStorage.
    }
}

function createUuid() {
    if (typeof crypto.randomUUID === "function") return crypto.randomUUID();

    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
    bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) =>
        byte.toString(16).padStart(2, "0"),
    ).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

async function hashStorageKey(value: string) {
    const digest = await crypto.subtle.digest(
        "SHA-256",
        new TextEncoder().encode(value),
    );
    return Array.from(new Uint8Array(digest), (byte) =>
        byte.toString(16).padStart(2, "0"),
    ).join("");
}

function getReferralStartParam() {
    const webAppStartParam = getTelegramWebApp()?.initDataUnsafe.start_param;
    const queryStartParam = new URLSearchParams(window.location.search).get(
        "tgWebAppStartParam",
    );
    return (
        [webAppStartParam, queryStartParam].find(
            (candidate): candidate is string =>
                typeof candidate === "string" &&
                REFERRAL_START_PARAM.test(candidate),
        ) ?? null
    );
}

function errorMessage(error: unknown) {
    return error instanceof Error ? error.message : i18n.t("errors.generic");
}

// StrictMode can remount the campaign hook while a redemption is in flight.
const redeemingInvites = new Set<string>();

export function useReferralCampaign({
    enabled,
    isOpen,
    userId,
    telegramUserId,
}: UseReferralCampaignOptions) {
    const posthog = usePostHog();
    const requestSlotMachineRefetch = useRequestSlotMachineRefetch();
    const webAppTelegramUserId =
        getTelegramWebApp()?.initDataUnsafe.user?.id.toString();
    const storageScope =
        userId ??
        telegramUserId?.toString() ??
        webAppTelegramUserId ??
        "telegram";
    const storagePrefix = useMemo(
        () => `${NAME_SPACE}referral:${REFERRAL_CAMPAIGN_CODE}:${storageScope}`,
        [storageScope],
    );
    const requestIdKey = `${storagePrefix}:request-id`;
    const shareHintKey = `${storagePrefix}:share-reported`;
    const pendingCompleteKey = `${storagePrefix}:pending-complete`;

    const [referral, setReferral] = useState<ApiReferralSnapshot | null>(null);
    const [preparedShare, setPreparedShare] = useState<PreparedShare | null>(
        null,
    );
    const [isLoading, setIsLoading] = useState(false);
    const [isPreparing, setIsPreparing] = useState(false);
    const [isReportingShare, setIsReportingShare] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [hasLocalShareHint, setHasLocalShareHint] = useState(
        () => readStorage(shareHintKey) === "1",
    );
    const mountedRef = useRef(false);
    const statusRequestRef = useRef<AbortController | null>(null);
    const preparePromiseRef = useRef<Promise<void> | null>(null);
    const rewardedAtRef = useRef<string | null | undefined>(undefined);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            statusRequestRef.current?.abort();
        };
    }, []);

    useEffect(() => {
        setHasLocalShareHint(readStorage(shareHintKey) === "1");
    }, [shareHintKey]);

    const applyReferral = useCallback(
        (next: ApiReferralSnapshot) => {
            if (!mountedRef.current) return;

            setReferral(next);
            const rewardedAt = next.reward.awardedAt;
            const hadReferral = rewardedAtRef.current !== undefined;
            if (rewardedAt && rewardedAtRef.current !== rewardedAt) {
                requestSlotMachineRefetch();
                if (hadReferral) haptics.notification("success");
            }
            rewardedAtRef.current = rewardedAt;
        },
        [requestSlotMachineRefetch],
    );

    const refreshStatus = useCallback(async () => {
        if (!enabled) return null;

        statusRequestRef.current?.abort();
        const controller = new AbortController();
        statusRequestRef.current = controller;
        if (mountedRef.current) setIsLoading(true);

        try {
            const { body } = await authenticatedApiCall(
                "/api/v1/referrals/share",
                ApiReferralStatusResSchema,
                { signal: controller.signal },
            );
            if (body.status === "ERROR") {
                throw new ReferralApiError(body.code, body.message);
            }
            if (controller.signal.aborted) return null;
            applyReferral(body.referral);
            if (mountedRef.current) setError(null);
            return body.referral;
        } catch (statusError) {
            if (!controller.signal.aborted && mountedRef.current) {
                setError(errorMessage(statusError));
            }
            return null;
        } finally {
            if (mountedRef.current && statusRequestRef.current === controller) {
                statusRequestRef.current = null;
                setIsLoading(false);
            }
        }
    }, [applyReferral, enabled]);

    useEffect(() => {
        if (!enabled) return;

        // The launcher needs server status even while the drawer is closed.
        void refreshStatus();
        const refreshWhenVisible = () => {
            if (document.visibilityState === "visible") void refreshStatus();
        };
        document.addEventListener("visibilitychange", refreshWhenVisible);
        return () => {
            document.removeEventListener(
                "visibilitychange",
                refreshWhenVisible,
            );
            statusRequestRef.current?.abort();
        };
    }, [enabled, refreshStatus]);

    const prepareShare = useCallback(
        async (rotateRequestId = false) => {
            if (!enabled) return;
            if (
                !rotateRequestId &&
                preparedShare &&
                new Date(preparedShare.expiresAt).getTime() > Date.now()
            ) {
                return;
            }
            if (preparePromiseRef.current) return preparePromiseRef.current;

            const operation = (async () => {
                if (mountedRef.current) {
                    setIsPreparing(true);
                    setError(null);
                }
                if (rotateRequestId) removeStorage(requestIdKey);
                const storedRequest = ApiReferralPrepareReqSchema.safeParse({
                    requestId: readStorage(requestIdKey),
                });
                const request = storedRequest.success
                    ? storedRequest.data
                    : ApiReferralPrepareReqSchema.parse({
                          requestId: createUuid(),
                      });
                writeStorage(requestIdKey, request.requestId);

                const { body } = await authenticatedApiCall(
                    "/api/v1/referrals/share",
                    ApiReferralPrepareResSchema,
                    {
                        method: "POST",
                        body: JSON.stringify(request),
                    },
                );
                if (body.status === "ERROR") {
                    throw new ReferralApiError(body.code, body.message);
                }
                if (!body.referral.shareMessageId) {
                    throw new ReferralApiError(
                        "TELEGRAM_UNAVAILABLE",
                        i18n.t("errors.preparedInviteMissing"),
                    );
                }
                if (!mountedRef.current) return;
                setPreparedShare({
                    messageId: body.preparedMessageId,
                    expiresAt: body.preparedMessageExpiresAt,
                    shareMessageId: body.referral.shareMessageId,
                });
                applyReferral(body.referral);
            })()
                .catch((prepareError: unknown) => {
                    if (mountedRef.current) {
                        setError(errorMessage(prepareError));
                    }
                })
                .finally(() => {
                    preparePromiseRef.current = null;
                    if (mountedRef.current) setIsPreparing(false);
                });

            preparePromiseRef.current = operation;
            return operation;
        },
        [applyReferral, enabled, preparedShare, requestIdKey],
    );

    useEffect(() => {
        if (!enabled || !isOpen) return;

        void refreshStatus().then((snapshot) => {
            if (!snapshot) return;
            if (snapshot.state === "EXPIRED") {
                setPreparedShare(null);
                void prepareShare(true);
            } else if (
                snapshot.state !== "REWARDED" &&
                snapshot.state !== "UNAVAILABLE"
            ) {
                void prepareShare();
            }
        });

        const pollId = window.setInterval(() => {
            void refreshStatus();
        }, POLL_INTERVAL_MS);
        return () => window.clearInterval(pollId);
    }, [enabled, isOpen, prepareShare, refreshStatus]);

    useEffect(() => {
        if (!enabled) return;

        const token = getReferralStartParam();
        if (!token) return;
        const redemptionAttemptKey = `${storagePrefix}:${token}`;
        if (redeemingInvites.has(redemptionAttemptKey)) return;
        redeemingInvites.add(redemptionAttemptKey);
        void (async () => {
            // Persist only a digest as the local idempotency hint. The raw
            // bearer token remains confined to Telegram's launch context and
            // this authenticated redemption request.
            const redeemedKey = `${storagePrefix}:redeemed:${await hashStorageKey(token)}`;
            if (readStorage(redeemedKey) === "1") return;

            const request = ApiReferralRedeemReqSchema.parse({ token });
            const { body } = await authenticatedApiCall(
                "/api/v1/referrals/redeem",
                ApiReferralRedeemResSchema,
                {
                    method: "POST",
                    body: JSON.stringify(request),
                },
            );
            if (body.status === "ERROR") {
                throw new ReferralApiError(body.code, body.message);
            }
            writeStorage(redeemedKey, "1");
            if (body.result === "QUALIFIED") {
                haptics.notification("success");
            }
            if (isOpen) void refreshStatus();
        })()
            .catch((redeemError: unknown) => {
                console.warn(
                    "[useReferralCampaign] invite redemption failed",
                    redeemError,
                );
            })
            .finally(() => redeemingInvites.delete(redemptionAttemptKey));
    }, [enabled, isOpen, refreshStatus, storagePrefix]);

    const reportShare = useCallback(
        async (shareMessageId: string, idempotencyKey: string) => {
            if (mountedRef.current) {
                setIsReportingShare(true);
                setError(null);
            }
            try {
                const request = ApiReferralShareCompleteReqSchema.parse({
                    shareMessageId,
                    idempotencyKey,
                });
                const { body } = await authenticatedApiCall(
                    "/api/v1/referrals/share/complete",
                    ApiReferralShareCompleteResSchema,
                    {
                        method: "POST",
                        body: JSON.stringify(request),
                    },
                );
                if (body.status === "ERROR") {
                    throw new ReferralApiError(body.code, body.message);
                }
                writeStorage(shareHintKey, "1");
                removeStorage(pendingCompleteKey);
                if (mountedRef.current) setHasLocalShareHint(true);
                applyReferral(body.referral);
            } catch (shareError) {
                if (mountedRef.current) setError(errorMessage(shareError));
            } finally {
                if (mountedRef.current) setIsReportingShare(false);
            }
        },
        [applyReferral, pendingCompleteKey, shareHintKey],
    );

    useEffect(() => {
        if (!enabled) return;

        const pendingJson = readStorage(pendingCompleteKey);
        if (!pendingJson) return;
        try {
            const pending = ApiReferralShareCompleteReqSchema.parse(
                JSON.parse(pendingJson),
            );
            void reportShare(pending.shareMessageId, pending.idempotencyKey);
        } catch {
            removeStorage(pendingCompleteKey);
        }
    }, [enabled, pendingCompleteKey, reportShare]);

    const share = useCallback(() => {
        if (!enabled || isPreparing || isReportingShare) return;
        if (
            !preparedShare ||
            new Date(preparedShare.expiresAt).getTime() <= Date.now()
        ) {
            setPreparedShare(null);
            void prepareShare();
            return;
        }

        const webApp = getTelegramWebApp();
        if (
            !webApp ||
            !webApp.isVersionAtLeast("8.0") ||
            typeof webApp.shareMessage !== "function"
        ) {
            const opened = shareUrlViaTelegram(
                TELEGRAM_MINI_APP_URL,
                i18n.t("referralDrawer.shareText"),
            );
            if (opened) {
                captureClientAnalytics(
                    posthog,
                    "referral_share_initiated",
                    {
                        campaign_code: REFERRAL_CAMPAIGN_CODE,
                        channel: "TELEGRAM_LINK",
                    },
                );
            }
            setError(i18n.t("errors.telegramVersionUnsupported"));
            return;
        }

        const idempotencyKey = createUuid();
        const completion = {
            shareMessageId: preparedShare.shareMessageId,
            idempotencyKey,
        };
        setError(null);

        try {
            // This call intentionally stays in the click stack: Telegram may
            // require a user gesture before it opens its native recipient UI.
            captureClientAnalytics(posthog, "referral_share_initiated", {
                campaign_code: REFERRAL_CAMPAIGN_CODE,
                channel: "TELEGRAM_NATIVE",
            });
            webApp.shareMessage(preparedShare.messageId, (sent) => {
                if (!sent) {
                    return;
                }
                writeStorage(shareHintKey, "1");
                if (mountedRef.current) setHasLocalShareHint(true);
                writeStorage(pendingCompleteKey, JSON.stringify(completion));
                void reportShare(preparedShare.shareMessageId, idempotencyKey);
            });
        } catch (shareError) {
            removeStorage(pendingCompleteKey);
            setError(errorMessage(shareError));
        }
    }, [
        enabled,
        isPreparing,
        isReportingShare,
        pendingCompleteKey,
        posthog,
        prepareShare,
        preparedShare,
        reportShare,
        shareHintKey,
    ]);

    return {
        referral,
        isLoading,
        isPreparing,
        isReportingShare,
        isShareReady:
            !!preparedShare &&
            new Date(preparedShare.expiresAt).getTime() > Date.now(),
        hasLocalShareHint,
        error,
        share,
        refreshStatus,
    };
}

export type ReferralCampaignController = ReturnType<typeof useReferralCampaign>;
